// The callouts (spec §10): pills with leaders over the viewport, placed from the anchors the
// viewport reports in `labels` events (callouts.ts places them). The DOM is updated directly on each
// event, and again from the last anchors when the UI around them changes (a dialog opens, a panel
// folds); React only owns the container.

import { useEffect, useRef } from 'react';
import { ANCHOR_BEHIND, ANCHOR_IN_VIEW, ANCHOR_STRIDE, ANCHOR_VISIBLE } from '../viewport/api.ts';
import { on } from '../viewport/events.ts';
import { trackedLabels } from '../viewport/bridge.ts';
import { useAtlas, type AtlasState } from '../app/store.ts';
import { calloutName } from '../data/shortNames.ts';
import { viewCallouts } from '../scenarios/index.ts';
import { inflate, place, type Candidate, type Placement, type Rect } from './callouts.ts';
import { siteRects } from './ElectrodeSites.tsx';

const css = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
const num = (name: string, fallback: number) => {
  const v = parseFloat(css(name));
  return Number.isFinite(v) ? v : fallback;
};

export interface Budget {
  rest: number;
  focus: number;
  max: number;
}

/** Callouts shown at once (`hud.budget`): six at rest so the picture leads, twelve once something
 *  is selected, isolated or matched by search, twenty-four with Label density Many. Few is six
 *  everywhere. Presentation keeps six (twelve with Many). */
export function budget(s: Pick<AtlasState, 'labelDensity' | 'presenting' | 'selected' | 'isolate' | 'matches'>, b: Budget = { rest: 6, focus: 12, max: 24 }): number {
  if (s.labelDensity === 'few') return b.rest;
  if (s.labelDensity === 'many') return s.presenting ? b.focus : b.max;
  if (s.presenting) return b.rest;
  return s.selected || s.isolate || s.matches ? b.focus : b.rest;
}

/** The HUD's numbers, from the tokens (with their 1.3.0 values if a sheet is missing). */
function hudTokens() {
  const font = (role: string) => `${css(`--ba-type-${role}-weight`) || 530} ${css(`--ba-type-${role}-size`) || '13px'} ${css(`--ba-type-${role}-family`) || 'Inter, system-ui, sans-serif'}`;
  const tracking = (role: string, size: number) => (parseFloat(css(`--ba-type-${role}-tracking`)) || 0) * size;
  return {
    budget: { rest: num('--ba-hud-budget-rest', 6), focus: num('--ba-hud-budget-focus', 12), max: num('--ba-hud-budget-max', 24) },
    minArea: num('--ba-hud-budget-min-area-px2', 900),
    gap: num('--ba-hud-budget-gap', 8),
    fade: num('--ba-hud-budget-fade', 150),
    margin: num('--ba-size-safe-margin', 24),
    height: num('--ba-hud-label-height', 22),
    padX: parseFloat(css('--ba-hud-label-padding').split(/\s+/)[1] ?? '') || 9,
    sideGap: num('--ba-space-1', 4),
    anchor: num('--ba-hud-anchor-radius', 2.5),
    label: { font: font('hud-label'), tracking: tracking('hud-label', num('--ba-type-hud-label-size', 13)) },
    caption: { font: font('hud-caption'), tracking: tracking('hud-caption', num('--ba-type-hud-caption-size', 11)) },
  };
}

let measureCtx: CanvasRenderingContext2D | null = null;
const widths = new Map<string, number>();
function textWidth(text: string, type: { font: string; tracking: number }): number {
  const key = `${type.font}\n${text}`;
  let w = widths.get(key);
  if (w !== undefined) return w;
  measureCtx ??= document.createElement('canvas').getContext('2d');
  if (!measureCtx) return text.length * 7;
  measureCtx.font = type.font;
  w = Math.ceil(measureCtx.measureText(text).width + text.length * type.tracking);
  widths.set(key, w);
  return w;
}
// Widths measured before the UI font arrived are a fallback face's.
if (typeof document !== 'undefined') document.fonts?.addEventListener('loadingdone', () => widths.clear());

/** Where the callouts may not go, grown by the safe margin: the bar, the panels, the bottom row
 *  (the Methods caption, the timeline, the HUD corner's caption, scale bar and gnomon), and dialogs
 *  that set data-label-exclude (the search palette, the shortcuts sheet). */
function exclusions(root: HTMLElement, margin: number): Rect[] {
  const base = root.getBoundingClientRect();
  const presenting = useAtlas.getState().presenting;
  return [...document.querySelectorAll<HTMLElement>('[data-inset]:not(.hud), .hud > *, [data-label-exclude]')]
    // getClientRects, not offsetParent: dialogs are position: fixed, which has no offsetParent.
    .filter((el) => !el.classList.contains('collapsed') && el.getClientRects().length > 0)
    .filter((el) => !(presenting && el.matches('.topbar, .panel, .timeline')))
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0)
    .map((r) => inflate({ x: r.left - base.left, y: r.top - base.top, w: r.width, h: r.height }, margin));
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
const pathD = (p: Placement) => p.path.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join('');

interface Callout extends Candidate {
  text: string;
  /** Selected but with no visible pixels: anchored at its projected centroid, the leader dashed. */
  hidden: boolean;
  kind: 'selected' | 'hover' | '';
}

export function Labels() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const T = hudTokens();
    const pool = new Map<string, { el: HTMLDivElement; key: string; out: number }>();
    const svg = root.querySelector('svg')!;
    // The anchors arrive in the order of the ids the bridge last sent; keep both together.
    let last: { anchors: Float32Array; ids: string[] } | null = null;
    let prev = new Map<string, Placement>();
    let dragging = false;
    let movedInDrag = false;

    const candidates = (s: AtlasState): Callout[] => {
      const cat = s.catalogue;
      if (!last || !cat) return [];
      const { anchors, ids } = last;
      const matches = new Set(s.matches ?? []);
      // A connectome pack's anchors are points (a soma, a neuropil's centre) with no area: they rank in
      // the order the bridge sent them, which is the catalogue's (data/tissue.ts).
      const points = cat.kind === 'connectome';
      const pinned = new Set(viewCallouts(s));
      const out: Callout[] = [];
      for (let k = 0; k < ids.length; k++) {
        const o = k * ANCHOR_STRIDE;
        if (o + ANCHOR_STRIDE > anchors.length) break;
        const flags = anchors[o + 4]!;
        const id = ids[k]!;
        const kind = id === s.selected ? 'selected' : id === s.hovered ? 'hover' : '';
        // Without visible pixels the anchor is the projected centroid: only the selection and the view's
        // own callouts are named there.
        const hidden = !(flags & ANCHOR_VISIBLE);
        if (!(flags & ANCHOR_IN_VIEW) || flags & ANCHOR_BEHIND || (hidden && kind !== 'selected' && !pinned.has(id))) continue;
        const area = anchors[o + 3]!;
        const rank = kind === 'selected' ? 3 : kind === 'hover' || pinned.has(id) ? 2 : matches.has(id) ? 1 : 0;
        if (!rank && ((!points && area < T.minArea) || s.matches)) continue;
        const st = cat.byId.get(id);
        if (!st) continue;
        const text = calloutName(st, s.brain?.short);
        const w = textWidth(text, T.label) + 2 * T.padX + (hidden ? T.sideGap + textWidth('· hidden', T.caption) : 0);
        out.push({ id, x: anchors[o]!, y: anchors[o + 1]!, w, h: T.height, prio: rank * 1e9 + (points ? ids.length - k : area), focus: kind !== '', text, hidden, kind });
      }
      return out;
    };

    const draw = (mode: 'hold' | 'follow' | 'fresh') => {
      const s = useAtlas.getState();
      if (!last || !s.catalogue || !s.labelsOn) {
        root.style.opacity = '0';
        return;
      }
      root.style.opacity = '1';
      const cands = candidates(s);
      const byId = new Map(cands.map((c) => [c.id, c]));
      const placed = place(cands, { frame: { w: root.clientWidth, h: root.clientHeight }, margin: T.margin, gap: T.gap, exclusions: exclusions(root, T.margin), avoid: siteRects.rects, limit: budget(s, T.budget), prev, mode });
      prev = new Map(placed.map((p) => [p.id, p]));
      const lines: string[] = [];
      for (const p of placed) {
        const c = byId.get(p.id)!;
        let item = pool.get(p.id);
        if (!item) {
          const el = document.createElement('div');
          el.className = 'label';
          el.dataset.id = p.id;
          root.appendChild(el);
          item = { el, key: '', out: 0 };
          pool.set(p.id, item);
        }
        if (item.out) {
          clearTimeout(item.out);
          item.out = 0;
          item.el.classList.remove('out');
        }
        const key = `${c.text}\n${c.hidden}`;
        if (item.key !== key) {
          item.key = key;
          item.el.textContent = c.text;
          if (c.hidden) {
            const side = document.createElement('span');
            side.className = 'label-side';
            side.textContent = '· hidden';
            item.el.appendChild(side);
          }
        }
        item.el.dataset.focus = c.kind;
        item.el.style.transform = `translate(${p.rect.x}px, ${p.rect.y}px)`;
        item.el.style.width = `${p.rect.w}px`;
        const [ax, ay] = p.path[0]!;
        lines.push(`<g data-id="${esc(p.id)}"><path class="leader${c.hidden ? ' hidden' : ''}" d="${pathD(p)}"/><circle class="anchor" cx="${ax.toFixed(1)}" cy="${ay.toFixed(1)}" r="${T.anchor}"/></g>`);
      }
      // Pills that leave fade out over hud.budget.fade, as new ones fade in.
      for (const [id, item] of pool)
        if (!prev.has(id) && !item.out) {
          item.el.classList.add('out');
          item.out = window.setTimeout(() => {
            item.el.remove();
            pool.delete(id);
          }, T.fade);
        }
      svg.innerHTML = lines.join('');
    };

    // Once the picture is still, a plain first fit, so the callouts at rest are the spec's (and the screens').
    let settle = 0;
    const settled = () => {
      clearTimeout(settle);
      settle = window.setTimeout(() => !dragging && draw('fresh'), 250);
    };
    const off = on('labels', (e) => {
      // Event arrays are only valid during the call.
      last = { anchors: new Float32Array(e.anchors), ids: trackedLabels.ids.slice() };
      if (dragging) movedInDrag = true;
      draw(dragging ? 'follow' : 'hold');
      settled();
    });
    // A drag on the canvas carries the callouts with their anchors; they re-plan on release.
    const down = (e: PointerEvent) => {
      if ((e.target as Element | null)?.matches?.('canvas.viewport')) {
        dragging = true;
        movedInDrag = false;
      }
    };
    const up = () => {
      if (!dragging) return;
      dragging = false;
      if (movedInDrag) draw('fresh');
    };
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', up, true);
    // Dialogs and panels change what the callouts must avoid; place again once they've laid out,
    // and once more when their open transition (--dur-panel) has finished.
    let raf = 0;
    let timer = 0;
    const replace = () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      raf = requestAnimationFrame(() => draw('hold'));
      timer = window.setTimeout(() => draw('hold'), 340);
    };
    const unsub = useAtlas.subscribe(
      (s) => [s.paletteOpen, s.helpOpen, s.methodsOpen, s.panels, s.presenting, s.labelsOn, s.labelDensity, s.isolate, s.selected, s.hovered, s.matches, s.brain, s.preset] as const,
      replace,
      { equalityFn: (a, b) => a.every((v, i) => v === b[i]) },
    );
    return () => {
      off();
      unsub();
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', up, true);
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      clearTimeout(settle);
      for (const { el, out } of pool.values()) {
        clearTimeout(out);
        el.remove();
      }
    };
  }, []);
  return (
    <div ref={ref} className="labels" aria-hidden="true">
      <svg className="leaders" />
    </div>
  );
}
